import React from 'react';
import PropTypes from 'prop-types';
import { WalteroButton } from '../waltero';

const PerformanceImageModal = ({ 
  isOpen, 
  selectedImage, 
  modalPictures, 
  currentImageIndex,
  onClose, 
  onNextImage, 
  onPrevImage 
}) => {
  if (!isOpen || !selectedImage) {
    return null;
  }

  const hasMultipleImages = modalPictures && modalPictures.length > 1;
  const hasPrevious = hasMultipleImages && currentImageIndex > 0;
  const hasNext = hasMultipleImages && currentImageIndex < modalPictures.length - 1;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        if (hasPrevious && onPrevImage) {
          onPrevImage();
        }
        break;
      case 'ArrowRight':
        if (hasNext && onNextImage) {
          onNextImage();
        }
        break;
    }
  };

  return (
    <div 
      className="image-modal-overlay" 
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Device image viewer"
      tabIndex={-1}
    >
      <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="image-modal-header">
          <h3>Device Image - {selectedImage.deviceId}</h3>
          <button 
            className="image-modal-close"
            onClick={onClose}
            aria-label="Close image modal"
            type="button"
          >
            ×
          </button>
        </div>
        
        <div className="image-modal-body">
          <img 
            src={selectedImage.url} 
            alt="Device sensor view"
            className="image-modal-image"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div className="image-modal-fallback" style={{ display: 'none' }}>
            <p>Failed to load image</p>
          </div>
          
          {selectedImage.lastModified && (
            <p className="image-modal-timestamp">
              Captured: {new Date(selectedImage.lastModified).toLocaleString()}
            </p>
          )}
        </div>
        
        {/* Navigation controls for multiple images */}
        {hasMultipleImages && (
          <div className="image-modal-navigation">
            <WalteroButton
              variant="outlined"
              onClick={onPrevImage}
              disabled={!hasPrevious}
              aria-label="Previous image"
            >
              Previous
            </WalteroButton>
            
            <span className="image-counter">
              {currentImageIndex + 1} of {modalPictures.length}
            </span>
            
            <WalteroButton
              variant="outlined"
              onClick={onNextImage}
              disabled={!hasNext}
              aria-label="Next image"
            >
              Next
            </WalteroButton>
          </div>
        )}
      </div>
    </div>
  );
};

PerformanceImageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  selectedImage: PropTypes.shape({
    url: PropTypes.string.isRequired,
    deviceId: PropTypes.string,
    lastModified: PropTypes.string
  }),
  modalPictures: PropTypes.arrayOf(PropTypes.object),
  currentImageIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onNextImage: PropTypes.func,
  onPrevImage: PropTypes.func
};

PerformanceImageModal.defaultProps = {
  selectedImage: null,
  modalPictures: [],
  currentImageIndex: 0,
  onNextImage: () => {},
  onPrevImage: () => {}
};

export default PerformanceImageModal;